import React, { SVGAttributes } from 'react';
import { CircleIcons } from '@tajawal/ct-web-design-system';

// hooks
import { useTranslation } from 'react-i18next';

// utils
import { getFareFamiliesFareRuleContent } from '@/utils/content.utils';
import { buildTestId } from '@/utils/helpers.utils';

// constants
import { FARE_RULES_ICONS } from '@/constants/content.constants';

// types
import { RuleSummaryModel } from '@tajawal/web-flights-sdk/lib/types/fare-rules.types';

// styles
import Styled from './FareRuleItem.styled';
import { BrandedFareRuleModel } from '@tajawal/web-flights-sdk/lib/types/fare-families.types';
import RuleItem from '../RuleItem/RuleItem';
import { GenericCallback } from '@/types/common.types';

const IconMap = {
  checkMarkIcon: CircleIcons.Check,
  feesIcon: CircleIcons.Fees,
  cancelIcon: CircleIcons.Close,
  notAvailableIcon: CircleIcons.Close,
  infoIcon: CircleIcons.Info,
  warningInfoIcon: CircleIcons.Attention,
};

type FareRuleItemProps = {
  rule: RuleSummaryModel | BrandedFareRuleModel;
  fareTag?: string;
  isChangeRule?: boolean;
  isCreditShell?: boolean;
  isLoading?: boolean;
  testIdPrefix: string;
  isWhiteIcon?: boolean;
  onInfoClick?: GenericCallback;
};

// TODO: Found duplicated components for the same implementation. Unify the components
export default function FareRuleItem({
  rule,
  fareTag,
  isChangeRule,
  isCreditShell,
  isLoading,
  testIdPrefix,
  isWhiteIcon = false,
  onInfoClick,
}: FareRuleItemProps): JSX.Element {
  const { t } = useTranslation();
  const { isAvailable, withFees } = rule || {};
  const testId = buildTestId(testIdPrefix, 'FareRules', isChangeRule ? 'Change' : 'Cancellation');
  let Icon: React.FC<SVGAttributes<any>>;

  if (isLoading) {
    return <Styled.FareItemLoading />;
  }

  if (fareTag) {
    Icon = IconMap[FARE_RULES_ICONS[fareTag]];
    return (
      <RuleItem
        icon={<Icon width="24" height="24" />}
        message={isChangeRule ? t(`FareRules.Change_${fareTag}`) : t(`FareRules.Cancellation_${fareTag}`)}
        isTextDimmed={!(isAvailable || fareTag === 'INFO_UNAVAILABLE_GDS')}
        testIdPrefix={testId}
        isWhiteIcon={isWhiteIcon}
        onInfoClick={onInfoClick}
      />
    );
  }

  if (withFees) {
    if (isCreditShell && !isChangeRule) {
      Icon = CircleIcons.Info;
    } else {
      Icon = CircleIcons.Fees;
    }
  } else if (isAvailable) {
    Icon = CircleIcons.Check;
  } else {
    Icon = CircleIcons.Close;
  }

  return (
    <RuleItem
      icon={<Icon width="24" height="24" />}
      message={getFareFamiliesFareRuleContent({
        isAvailable,
        withFees,
        isChangeRule,
        isCreditShell,
        t,
      })}
      isTextDimmed={!isAvailable}
      testIdPrefix={testId}
      isWhiteIcon={isWhiteIcon}
      onInfoClick={onInfoClick}
    />
  );
}
